/**
 * ReplayBreadcrumb Component
 *
 * Breadcrumb navigation for the drill-down hierarchy in the replay UI.
 * When teachers drill down into a moment to see individual edits, this
 * component shows where they are and provides a way to navigate back.
 *
 * Only renders when drilled down (currentMoment is not null).
 *
 * Follows WAI-ARIA Authoring Practices for breadcrumb navigation:
 * - Uses nav with aria-label="Breadcrumb"
 * - Uses ordered list for hierarchical structure
 * - Current location has aria-current="page"
 * - Truncated text is accessible via sr-only span
 *
 * @example
 * ```tsx
 * <ReplayBreadcrumb
 *   currentMoment={selectedMoment}
 *   momentIndex={5}
 *   onPopUp={() => setSelectedMoment(null)}
 * />
 * ```
 */

import type { KeyMoment } from "../types";

/** Maximum character length for summary display before truncation */
const MAX_SUMMARY_LENGTH = 40;

/**
 * Strings for the Breadcrumb component.
 * These can be overridden via props for i18n.
 */
export interface ReplayBreadcrumbStrings {
  keyMoments: string;
  backToKeyMoments: string;
  moment: (index: number, summary: string) => string;
}

const defaultStrings: ReplayBreadcrumbStrings = {
  keyMoments: "Key Moments",
  backToKeyMoments: "Back to Key Moments",
  moment: (index, summary) => `Moment ${index}: ${summary}`,
};

export interface ReplayBreadcrumbProps {
  /** Current moment being viewed (when drilled down). Null means not drilled down. */
  currentMoment: KeyMoment | null;
  /** Moment index (1-based for display) */
  momentIndex: number;
  /** Called when user clicks to go back to key moments */
  onPopUp: () => void;
  /** Optional className */
  className?: string;
  /** Override strings for i18n */
  strings?: Partial<ReplayBreadcrumbStrings>;
}

/**
 * Truncates a string to a maximum length, adding ellipsis if needed.
 */
function truncateSummary(summary: string, maxLength: number = MAX_SUMMARY_LENGTH): string {
  if (summary.length <= maxLength) {
    return summary;
  }
  return summary.slice(0, maxLength - 1).trimEnd() + "\u2026";
}

export function ReplayBreadcrumb({
  currentMoment,
  momentIndex,
  onPopUp,
  className = "",
  strings: stringOverrides,
}: ReplayBreadcrumbProps) {
  const strings = { ...defaultStrings, ...stringOverrides };

  // Don't render when not drilled down
  if (!currentMoment) {
    return null;
  }

  const displayName = strings.moment(momentIndex, truncateSummary(currentMoment.summary));
  const fullTitle = strings.moment(momentIndex, currentMoment.summary);
  const isTruncated = currentMoment.summary.length > MAX_SUMMARY_LENGTH;

  return (
    <nav
      className={`text-sm ${className}`}
      aria-label="Breadcrumb"
    >
      <ol className="flex items-center">
        <li>
          <button
            type="button"
            onClick={onPopUp}
            className="cursor-pointer text-brand-600 hover:text-brand-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1"
            aria-label={strings.backToKeyMoments}
          >
            {strings.keyMoments}
          </button>
        </li>
        <li className="flex items-center">
          <span className="mx-2 text-theme-muted" aria-hidden="true">
            {"\u203A"}
          </span>
          <span
            className="truncate font-medium text-theme-primary"
            title={isTruncated ? fullTitle : undefined}
            aria-current="page"
          >
            {/* Screen reader gets full text, visual display may be truncated */}
            {isTruncated && <span className="sr-only">{fullTitle}</span>}
            <span aria-hidden={isTruncated ? "true" : undefined}>{displayName}</span>
          </span>
        </li>
      </ol>
    </nav>
  );
}

export default ReplayBreadcrumb;
